import { isDocApplication } from '@/utils/app.js'
import { availableMenuFeatures } from '@/utils/featureset.js'

export const namespaced = true

export const state = {
  plainTree: {
    root: {
      code: 'root',
      parent: null
    },
    dashboard: {
      code: 'dashboard',
      route: { name: 'dashboard' },
      title: 'Дашборд',
      parent: 'root',
      feature: 'dashboard'
    },
    claims: {
      code: 'claims',
      title: 'Заявки',
      parent: 'root',
      feature: 'claim'
    },
    claims_list: {
      code: 'claims_list',
      route: { name: 'claims' },
      title: 'Лента',
      parent: 'claims',
      feature: 'claim'
    },
    claims_journal: {
      code: 'claims_journal',
      route: { name: 'claims_journal' },
      title: 'Журнал',
      parent: 'claims',
      feature: 'claim.journal'
    },
    procedures: {
      code: 'procedures',
      route: { name: 'procedures' },
      title: 'Торги',
      parent: 'root',
      feature: 'procedure'
    },
    contracts_group: {
      code: 'contracts_group',
      title: 'Договоры',
      parent: 'root',
      feature: 'contract'
    },
    contracts: {
      code: 'contracts',
      route: { name: 'contracts', query: { direction: 'all' } },
      title: 'Лента',
      parent: 'contracts_group',
      feature: 'contract'
    },
    document_templates: {
      code: 'document_templates',
      route: { name: 'document_templates' },
      title: 'Шаблоны',
      parent: 'contracts_group',
      feature: 'contract.template'
    },
    contracts_journal: {
      code: 'contracts_journal',
      route: { name: 'contracts_journal' },
      title: 'Журнал',
      parent: 'contracts_group',
      feature: 'contract.journal'
    },
    orders_group: {
      code: 'orders_group',
      title: 'Заказы',
      parent: 'root',
      feature: 'order'
    },
    orders: {
      code: 'orders',
      route: { name: 'orders', query: { direction: 'all' } },
      title: 'Лента',
      parent: 'orders_group',
      feature: 'order'
    },
    orders_journal: {
      code: 'orders_journal',
      route: { name: 'orders_journal' },
      title: 'Журнал',
      parent: 'orders_group',
      feature: 'order.journal'
    },
    reports: {
      code: 'reports',
      route: { name: 'reports' },
      title: 'Отчеты',
      parent: 'root',
      feature: 'report'
    },
    help: {
      code: 'help',
      route: { name: 'help' },
      title: 'Справка',
      parent: 'root',
      feature: 'help'
    },
    settings: {
      code: 'settings',
      route: { name: 'settings' },
      title: 'Настройки',
      parent: 'root',
      feature: 'settings'
    },
    about: {
      code: 'about',
      route: { name: 'about' },
      title: 'О системе',
      parent: 'root',
      feature: 'about'
    },
    drafts: {
      code: 'drafts',
      route: { name: 'drafts' },
      title: 'Черновик',
      parent: 'root',
      dev: true,
      feature: 'draft'
    },
    svg: {
      code: 'svg',
      route: { name: 'svg' },
      title: 'SVG',
      parent: 'root',
      dev: true,
      feature: 'svg'
    },
    colors: {
      code: 'colors',
      route: { name: 'colors' },
      title: 'Colors',
      parent: 'root',
      dev: true,
      feature: 'color'
    }
  },
  expandedNodes: isDocApplication() // раскрытые узлы
    ? new Set(['contracts_group', 'orders_group'])
    : new Set([]),
  hiddenNodes: new Set([]) // скрытые узлы (убрана галочка)
}

export const getters = {
  root (state) {
    return state.plainTree.root
  },
  accessibleTree (state, getters, rootGetters) {
    const plainTree = availableMenuFeatures(state.plainTree)
    // Дашборд доступен только в ДЕМО режиме
    // TODO: убрать delete tree.dashboard когда дашборд не будет грузить основную БД
    if (rootGetters.user.user.demo) {
      return plainTree
    } else {
      var tree = Object.assign({}, plainTree)
      delete tree.dashboard
      return tree
    }
  },
  children (state, getters) {
    return code => Object
      .values(getters.accessibleTree)
      .filter(node => node.parent === code)
      .filter(node => !state.hiddenNodes.has(node.code))
  },
  allChildren (state, getters) {
    return code => Object
      .values(getters.accessibleTree)
      .filter(node => node.parent === code)
  },
  expandable (state, getters) {
    return code => Object
      .values(getters.accessibleTree)
      .find(node => node.parent === code)
  },
  expanded (state) {
    return code => state.expandedNodes.has(code)
  },
  hidden (state) {
    return code => state.hiddenNodes.has(code)
  },
  firstTopLevelItem (state, getters) {
    const item = Object
      .values(getters.accessibleTree)
      .filter(node => node.route)
      .filter(node => !state.hiddenNodes.has(node.code))
      .shift()
    return item
  }
}

export const mutations = {
  TOGGLE_EXPANDING (state, code) {
    state.expandedNodes = state.expandedNodes.delete(code)
      ? new Set(state.expandedNodes)
      : new Set(state.expandedNodes.add(code))
  },
  HIDE_ITEM (state, { code, accessibleTree }) {
    var hiddenNodes = hideChildren(Object.values(accessibleTree), state.hiddenNodes.add(code), code)
    hiddenNodes = hideParents(accessibleTree, hiddenNodes, code)
    state.hiddenNodes = new Set(hiddenNodes) // реактивность
  },
  SHOW_ITEM (state, { code, accessibleTree }) {
    state.hiddenNodes.delete(code)
    var hiddenNodes = showChildren(Object.values(accessibleTree), state.hiddenNodes, code)
    hiddenNodes = showParents(accessibleTree, hiddenNodes, code)
    state.hiddenNodes = new Set(hiddenNodes)
  },
  SET_HIDDEN_SETTINGS (state, settings) {
    state.hiddenNodes = settings
  }
}

export const actions = {
  loadMenuSettings ({ state, commit, getters, rootGetters }) {
    commit('SET_HIDDEN_SETTINGS', loadHiddenSettings(rootGetters['user/user']))
  },
  toggleExpanding ({ commit }, code) {
    commit('TOGGLE_EXPANDING', code)
  },
  hideItem ({ state, commit, getters, rootGetters }, code) {
    commit('HIDE_ITEM', { code, accessibleTree: getters.accessibleTree })
    saveHiddenSettings(state.hiddenNodes, rootGetters['user/user'])
  },
  showItem ({ state, commit, getters, rootGetters }, code) {
    commit('SHOW_ITEM', { code, accessibleTree: getters.accessibleTree })
    saveHiddenSettings(state.hiddenNodes, rootGetters['user/user'])
  }
}

// localStorage UTILS

// TODO сделать какой-то общий объект для хранения в localStorage

function loadHiddenSettings (user) {
  if (user) {
    const settings = localStorage.getItem('MIK_SETTINGS')
    const json = settings && JSON.parse(settings)
    return json?.[user.id]?.codes instanceof Array
      ? new Set(json[user.id].codes)
      : new Set([])
  } else {
    return new Set([])
  }
}

function saveHiddenSettings (hiddenNodes, user) {
  if (user) {
    const settings = localStorage.getItem('MIK_SETTINGS')
    const json = (settings && JSON.parse(settings)) || {}
    localStorage.setItem('MIK_SETTINGS', JSON.stringify({ ...json, [user.id]: { codes: [...hiddenNodes] } }))
  }
}

// menu tree UTILS

function hideChildren (plainTreeValues, hiddenNodes, code) {
  const children = plainTreeValues.filter(node => node.parent === code) || []
  return children.reduce((hnAcc, child) => {
    return hideChildren(plainTreeValues, hnAcc.add(child.code), child.code)
  }, hiddenNodes)
}

function showChildren (plainTreeValues, hiddenNodes, code) {
  const children = plainTreeValues.filter(node => node.parent === code) || []
  return children.reduce((hnAcc, child) => {
    hnAcc.delete(child.code)
    return showChildren(plainTreeValues, hnAcc, child.code)
  }, hiddenNodes)
}

function hideParents (plainTree, hiddenNodes, code) {
  const parentCode = plainTree[code].parent
  if (parentCode === 'root') return hiddenNodes

  const hidden = Object
    .values(plainTree)
    .filter(node => node.parent === parentCode)
    .reduce((acc, sister) => acc && hiddenNodes.has(sister.code), true)

  hidden ? hiddenNodes.add(parentCode) : hiddenNodes.delete(parentCode)
  return hideParents(plainTree, hiddenNodes, parentCode)
}

function showParents (plainTree, hiddenNodes, code) {
  const parentCode = plainTree[code].parent
  if (parentCode === 'root') return hiddenNodes
  hiddenNodes.delete(parentCode)
  return showParents(plainTree, hiddenNodes, parentCode)
}
